import { useEffect, useRef, useState } from "react";
import AnimateOnScroll from "./AnimateOnScroll";

const features = [
  {
    img: "/assets/images/tablet.png",
    title: "Easy to Use",
    desc: "Easy to use conversational data input chatbox that generates accurate holistic financial forecasts in seconds.",
  },
  {
    img: "/assets/images/books.png",
    title: "Powerful AND Realistic",
    desc: "Most tools get the maths right. Ours also gets the life right. Plans are grounded in real-world behaviours and achievable outcomes — not just technically correct numbers your clients won't believe.",
  },
  {
    img: "/assets/images/settings.png",
    title: "Configurable",
    desc: "Reflect your brand and easily configure your professionally researched Central Investment Proposition.",
  },
];

export default function PlatformSection() {
  const [showVideo, setShowVideo] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (showVideo) {
      videoRef.current?.play();
    } else {
      const v = videoRef.current;
      if (v) {
        v.pause();
        v.currentTime = 0;
      }
    }
  }, [showVideo]);

  return (
    <section id="platform" className="bg-[#EFEBE5] py-24">
      <div className="max-w-6xl mx-auto px-6">
        <AnimateOnScroll variant="fade-up">
          <div className="text-center mb-16">
            <p className="text-xs font-bold tracking-widest text-brown-300 uppercase mb-4">
              Platform
            </p>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1a1a2e] mb-6">
              Built for Financial Planners.
            </h2>
            <p className="text-base text-[#6a6a7a] max-w-2xl mx-auto leading-relaxed">
              Generic AI platforms pose significant risks. In finance, plausible
              answers aren't enough — outcomes hinge on professional-grade
              accuracy.
            </p>
          </div>
        </AnimateOnScroll>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
          {features.map((f, i) => (
            <AnimateOnScroll key={f.title} variant="fade-up" delay={i * 100}>
              <div className="bg-brown-10 rounded-2xl p-6 shadow-lg hover:border-[#C8BFB0] transition-all duration-300 group h-full">
                <div className="w-32 h-32 mb-5 overflow-hidden rounded mx-auto">
                  <img
                    alt={f.title}
                    className="w-full h-full object-cover object-top"
                    src={f.img}
                  />
                </div>
                <h3 className="text-base font-bold text-[#1a1a2e] mb-3">
                  {f.title}
                </h3>
                <p className="text-md text-[#6a6a7a] leading-relaxed">
                  {f.desc}
                </p>
              </div>
            </AnimateOnScroll>
          ))}
        </div>

        <AnimateOnScroll variant="fade-up" delay={300}>
          <div className="mt-14 flex flex-col items-center gap-4 text-center">
            <p className="text-md text-[#6a6a7a]">Watch a sample demo.</p>

            <button
              onClick={() => setShowVideo(true)}
              className="inline-flex items-center gap-2 bg-[#333] text-white font-semibold text-md px-8 py-3 rounded-full hover:bg-[#000] transition-colors duration-200"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-4 h-4"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
              See it in action
            </button>
          </div>
        </AnimateOnScroll>

        {showVideo && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
            onClick={() => setShowVideo(false)}
          >
            <div
              className="relative w-full max-w-4xl mx-4 rounded-2xl overflow-hidden shadow-2xl bg-black"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="absolute z-10 pointer-events-none select-none"
                style={{ top: "9%", left: "2px" }}
              >
                <span className="text-xs font-semibold text-[#BF8C1A] whitespace-nowrap bg-[#FDF8F0] rounded-full px-2.5 py-1 shadow-sm inline-block">
                  your logo here
                </span>
              </div>
              <style>{`
                @keyframes bounceY {
                  0%, 100% { transform: translateY(0); }
                  50% { transform: translateY(-4px); }
                }
              `}</style>
              <button
                onClick={() => setShowVideo(false)}
                className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
                aria-label="Close"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    fill="none"
                  />
                </svg>
              </button>
              <video
                ref={videoRef}
                src="/assets/videos/envizage.ai_0.6@1080.mp4"
                controls
                className="w-full aspect-video"
              />
            </div>
          </div>
        )}

        {/* White-label report callout */}
        {false && (
          <AnimateOnScroll variant="fade-up" delay={120}>
            <div className="mt-8 rounded-2xl bg-white shadow-lg overflow-hidden grid grid-cols-1 lg:grid-cols-2 items-center">
              {/* Left — copy */}
              <div className="p-10 flex flex-col gap-5">
                <div className="inline-flex items-center gap-2 w-fit bg-brown-10 rounded px-3 py-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-file-paper-2-line text-sm text-brown-300"></i>
                  </div>
                  <span className="text-xs font-bold text-brown-300 uppercase tracking-wider whitespace-nowrap">
                    White-Label Reports
                  </span>
                </div>
                <h3 className="text-2xl lg:text-3xl font-bold text-[#1a1a2e] leading-tight">
                  Branded reports. Generated at the flick of a button.
                </h3>
                <p className="text-md text-[#6a6a7a] leading-relaxed">
                  Every planning session produces a polished, client-ready
                  report — branded with your firm's identity. Clear, jargon-free
                  layouts your clients can actually read and act on. No
                  formatting, no editing, no manual work.
                </p>
                <ul className="flex flex-col gap-3 mt-1">
                  {[
                    {
                      icon: "ri-magic-line",
                      text: "One-click generation from any completed session",
                    },
                    {
                      icon: "ri-layout-line",
                      text: "Clean, digestible layout — designed for clients, not planners",
                    },
                    {
                      icon: "ri-building-line",
                      text: "Your logo, your colours, your brand",
                    },
                    {
                      icon: "ri-file-download-line",
                      text: "Instant PDF — ready to share or email",
                    },
                  ].map((item) => (
                    <li key={item.text} className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brown-300 flex-shrink-0 mt-0.5">
                        <i
                          className={`${item.icon} text-xs text-brown-300`}
                        ></i>
                      </div>
                      <span className="text-md text-[#3a3a4a] leading-relaxed">
                        {item.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Right — mock report preview (Envizage green style) */}
              <div className="p-8 flex items-center justify-center min-h-[340px]">
                <div className="w-full shadow-lg max-w-sm bg-white rounded border border-[#E8F5EE] overflow-hidden">
                  {/* Report header */}
                  <div className="px-4 pt-3 pb-2 flex items-start justify-between border-b border-[#E8F5EE]">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-leaf-line text-base text-[#1BB184]"></i>
                    </div>
                    <div className="text-right">
                      <p className="text-md font-bold text-[#1a1a2e]">
                        Your planning report
                      </p>
                      <p className="text-[10px] text-[#00CC7A] font-medium">
                        March 25th 2026
                      </p>
                    </div>
                  </div>

                  {/* Section 02 — Projections */}
                  <div className="px-4 pt-3">
                    <div className="flex items-center gap-2 mb-2.5">
                      <span className="text-xl font-extrabold text-[#00CC7A] leading-none">
                        02
                      </span>
                      <span className="text-xs font-bold text-[#1a1a2e]">
                        Your projections
                      </span>
                    </div>

                    {/* Fake bar chart */}
                    <p className="text-[10px] font-semibold text-[#00CC7A] mb-1.5">
                      Projected wealth over time
                    </p>
                    <div className="flex items-end gap-1 h-14 mb-3">
                      {[30, 42, 55, 61, 70, 74, 82, 88, 95, 100].map((h, i) => (
                        <div
                          key={i}
                          className="flex-1 rounded-t"
                          style={{
                            height: `${h}%`,
                            background:
                              i >= 7
                                ? "#1BB184"
                                : i >= 4
                                  ? "#00CC7A"
                                  : "#7FEBB8",
                          }}
                        />
                      ))}
                    </div>

                    {/* Goals row */}
                    <p className="text-[10px] font-semibold text-[#00CC7A] mb-1.5">
                      Goal tracking
                    </p>
                    <div className="flex gap-1.5 mb-3">
                      {[
                        { label: "Retire at 67", status: "On track", ok: true },
                        {
                          label: "Clear mortgage",
                          status: "On track",
                          ok: true,
                        },
                        { label: "Child's uni", status: "On track", ok: true },
                        { label: "Legacy fund", status: "Review", ok: false },
                      ].map((g) => (
                        <div
                          key={g.label}
                          className="flex-1 border border-[#E8F5EE] rounded p-1.5"
                        >
                          <p className="text-[8px] text-[#9a9aaa] uppercase tracking-wide leading-tight mb-0.5">
                            GOAL
                          </p>
                          <p className="text-[9px] font-semibold text-[#1a1a2e] leading-tight mb-1">
                            {g.label}
                          </p>
                          <span
                            className="text-[8px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap"
                            style={{
                              background: g.ok ? "#E8F5EE" : "#FFF5E6",
                              color: g.ok ? "#1BB184" : "#c4875a",
                            }}
                          >
                            {g.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Bottom — stats + donut */}
                  <div className="px-4 pb-4 border-t border-[#E8F5EE] pt-3 flex items-center gap-3">
                    {/* Donut chart */}
                    <div className="flex-shrink-0">
                      <div className="relative w-12 h-12">
                        <div
                          className="w-12 h-12 rounded-full"
                          style={{
                            background:
                              "conic-gradient(#00CC7A 0% 55%, #1BB184 55% 80%, #7FEBB8 80% 100%)",
                          }}
                        ></div>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-7 h-7 rounded-full bg-white"></div>
                        </div>
                      </div>
                    </div>
                    {/* Stat row */}
                    <div className="flex-1 grid grid-cols-3 gap-2">
                      {[
                        { l: "Retire at", v: "67" },
                        { l: "Monthly", v: "£2,400" },
                        { l: "Projected", v: "£840K" },
                      ].map((s) => (
                        <div key={s.l} className="text-center">
                          <p className="text-[9px] text-[#9a9aaa] mb-0.5">
                            {s.l}
                          </p>
                          <p className="text-xs font-bold text-[#1a1a2e]">
                            {s.v}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </AnimateOnScroll>
        )}
      </div>
    </section>
  );
}
